import { api } from './client';
import type { DistanceTier, EventSummary, PromotionSummary, PublicAddress } from './types';
import type { TrustSummaryFacts } from '@/ui/kit/TrustSummary';

/** GET /community/impact (FR-049; mobile/src/services/communityApi.ts). */
export interface CommunityImpact {
  helpedThisMonth: number;
  totalMembers: number;
  neighborhoods: number;
  /** null until at least one post has been accepted by a helper. */
  avgResponseMinutes: number | null;
  /** specs/003 #2: the typical (median) minutes to a request's first response; absent from older servers. */
  medianResponseMinutes?: number | null;
  kudosGiven: number;
  /** "Need" posts created per day for the last 7 days, oldest first. */
  weeklyRequests: { day: string; count: number }[];
}

export interface NeighborSpotlight {
  memberId: string;
  name: string;
  photoUrl?: string;
  accountType: string;
  trustScore: number;
  /** specs/003 #8: facts shown instead of the percentage. */
  trustSummary?: TrustSummaryFacts | null;
  blurb: string;
}

export type HomeFeedFilter = 'all' | 'need' | 'offer' | 'promotion';

export interface HomeFeedItem {
  id: string;
  source: 'post' | 'promotion';
  kind: 'need' | 'offer' | 'promotion';
  title: string;
  category: string;
  ownerId: string;
  ownerName: string;
  ownerPhotoUrl?: string;
  distanceKm: number | null;
  /** specs/003 #1: shown instead of the figure. */
  distanceTier?: DistanceTier | null;
  createdAt: string;
  expiresAt?: string;
  /** specs/003 #4: a paid-for promotion, labelled "Sponsored" (never a neighbour's post). */
  sponsored?: boolean;
  /**
   * Posts only: `locked` while the help is going on (shown as Processing) and `completed` for a week after (Done);
   * absent from older servers.
   */
  status?: 'open' | 'locked' | 'completed';
}

export interface HomeFeed {
  items: HomeFeedItem[];
  promotionsCount: number;
  eventsCount: number;
  /** Present when a page was asked for (spec 008); an older server leaves them out. */
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
}

/** GET /members/:id/public-profile (mobile/src/services/promotionsEventsApi.ts). */
export interface PublicProfile {
  member: {
    id: string;
    name: string;
    accountType: string;
    trustScore?: number;
    /** specs/003 #8: concrete facts shown instead of the percentage (and how many neighbors vouched). */
    trustSummary?: TrustSummaryFacts | null;
    skills: string[];
    photoUrl?: string;
  };
  description?: string;
  businessCategory?: string;
  organizationType?: string;
  /** specs/003 #7: whether the business or organization itself is verified (absent for a person). */
  profileVerification?: 'verified' | 'unverified';
  /** Only when the business or organization chose to share it. */
  address?: PublicAddress | null;
  /** A website or social page (https) and, for a business, when it is open. */
  website?: string | null;
  openingHours?: string | null;
  promotions: PromotionSummary[];
  events: EventSummary[];
  memberSince?: string;
  bio?: string;
  helpedWith?: { category: string; count: number }[];
  kudos?: { label: string; count: number }[];
  thankYouNotes?: { fromName: string; note: string }[];
  otherPosts?: { id: string; kind: 'need' | 'offer'; title: string; category: string }[];
}

export const communityApi = {
  impact: () => api.get<CommunityImpact>('/community/impact'),
  spotlight: () => api.get<{ spotlight: NeighborSpotlight | null }>('/community/spotlight'),
  feed: (filter: HomeFeedFilter = 'all', page = 1) =>
    api.get<HomeFeed>(`/community/feed?filter=${encodeURIComponent(filter)}&page=${page}`),
  publicProfile: (memberId: string) =>
    api.get<PublicProfile>(`/members/${encodeURIComponent(memberId)}/public-profile`),
};
